'use client';

import type { ReactNode } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { MetaDefault } from '../../../../../components/typography';
import { useTranslations } from 'next-intl';
import type { CardRenderContext } from '../../tool-config-types';
import { FlatToolCard } from '../shared/flat-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import {
  HitlLabeledValue,
  HitlLabeledValues,
} from '../shared/hitl-labeled-values';
import { readStringValue, toRecord } from '../shared/network-tool-card-utils';

type ScopeValue = 'IN_SCOPE' | 'OUT_OF_SCOPE';

type ScopeCardArgs = {
  domains?: string[];
  netblocks?: string[];
  domainId?: string;
  netblockId?: string;
  scope?: ScopeValue;
};

function readStringArray(
  record: Record<string, unknown>,
  key: string,
): string[] | undefined {
  const value = record[key];

  if (!Array.isArray(value)) {
    return undefined;
  }

  const items = value.filter(
    (entry): entry is string =>
      typeof entry === 'string' && entry.trim().length > 0,
  );

  return items.length > 0 ? items : undefined;
}

function readScope(record: Record<string, unknown>): ScopeValue | undefined {
  const value = readStringValue(record, ['scope']);

  return value === 'IN_SCOPE' || value === 'OUT_OF_SCOPE' ? value : undefined;
}

function parseScopeArgs(input: unknown): ScopeCardArgs {
  const record = toRecord(input);

  if (!record) {
    return {};
  }

  return {
    domains: readStringArray(record, 'domains'),
    netblocks: readStringArray(record, 'netblocks'),
    domainId: readStringValue(record, ['domainId']),
    netblockId: readStringValue(record, ['netblockId']),
    scope: readScope(record),
  };
}

function hasScopeBody(args: ScopeCardArgs, error?: string): boolean {
  return Boolean(
    args.domains ||
      args.netblocks ||
      args.domainId ||
      args.netblockId ||
      args.scope ||
      error,
  );
}

function ScopeCardBody({
  args,
  error,
}: {
  args: ScopeCardArgs;
  error?: string;
}) {
  const t = useTranslations('designSystem.chat.externalNetworkScopeTool');
  const scopeLabel =
    args.scope === 'IN_SCOPE'
      ? t('scopeInScope')
      : args.scope === 'OUT_OF_SCOPE'
        ? t('scopeOutOfScope')
        : undefined;

  return (
    <VStack alignItems="stretch" gap={2} width="100%">
      {args.domains ? (
        <HitlLabeledValues label={t('domainsLabel')} values={args.domains} />
      ) : null}
      {args.netblocks ? (
        <HitlLabeledValues
          label={t('netblocksLabel')}
          values={args.netblocks}
        />
      ) : null}
      {args.domainId ? (
        <HitlLabeledValue label={t('domainLabel')} value={args.domainId} />
      ) : null}
      {args.netblockId ? (
        <HitlLabeledValue label={t('netblockLabel')} value={args.netblockId} />
      ) : null}
      {scopeLabel ? (
        <HitlLabeledValue label={t('scopeLabel')} value={scopeLabel} />
      ) : null}
      {error ? <MetaDefault color="fg.error">{error}</MetaDefault> : null}
    </VStack>
  );
}

export function renderExternalNetworkScopeCard(
  ctx: CardRenderContext,
): ReactNode {
  const { toolCallData, toolName } = ctx;
  const isStarted =
    toolCallData.status === 'started' || toolCallData.status === 'in-progress';

  if (
    !isStarted &&
    toolCallData.status !== 'completed' &&
    toolCallData.status !== 'error'
  ) {
    return undefined;
  }

  const args = parseScopeArgs(toolCallData.input);
  const error =
    toolCallData.status === 'error' ? toolCallData.error : undefined;

  return (
    <FlatToolCard
      title={toolName}
      isRunning={toolCallData.status === 'in-progress'}
      headerActions={getHITLArtifactHeaderActions(ctx)}
    >
      {hasScopeBody(args, error) ? (
        <ScopeCardBody args={args} error={error} />
      ) : undefined}
    </FlatToolCard>
  );
}
